import createOpenApiClient, { type Client, type Middleware } from 'openapi-fetch'
import { CSRF_HEADER, ensureCsrfCookie, needsCsrf, readCsrfToken } from './csrf'
import { ApiError, apiErrorFromResponse } from './errors'
import type { paths } from './schema'

export interface ApiClientOptions {
  /**
   * Base for the TYPED module routes, and it must already carry the `/api`
   * prefix when it names an origin.
   *
   * The generated `paths` in `schema.d.ts` are relative to the spec's server
   * URL, which ends in `/api` — the keys read `/accounting/accounts`, not
   * `/api/accounting/accounts`. openapi-fetch joins them to this value
   * verbatim, so a bare origin produces a 404 on every typed call.
   *
   * In development both apps leave this empty and let Vite proxy the request,
   * which is why the mistake above never showed up locally. See the comment in
   * each app's `src/lib/api.ts`.
   */
  baseUrl: string

  /**
   * Origin for the routes that sit OUTSIDE the `/api` prefix, e.g.
   * `https://api.multaqa.site`.
   *
   * Only `/sanctum/csrf-cookie` is fetched from here by this package; Fortify's
   * routes are the apps' own business. Defaults to `baseUrl`, which is correct
   * in development because both are the empty string there.
   */
  authOrigin?: string

  /** Headers evaluated for every request, so application locale changes apply immediately. */
  headers?: () => Record<string, string>

  /**
   * Called when the server says the session is gone (401).
   *
   * Redirecting to the login route is the app's decision, not this package's —
   * `admin` and `company` have different login routes. The error carries the
   * server's `code`, so an app can tell an ended support session
   * (`support_session_ended`) from a plain sign-out.
   */
  onUnauthorized?: (error: ApiError) => void

  /** Called for every response before this client converts an error response. */
  onResponse?: (response: Response) => void
}

export type ApiClient = Client<paths>

let lastRequestId: string | undefined

export function getLastRequestId(): string | undefined {
  return lastRequestId
}

/**
 * Build the configured client.
 *
 * `credentials: 'include'` on every request is what carries the Sanctum session
 * cookie. It also means the API must name our exact origin in
 * `config/cors.php` — a wildcard is rejected by the browser on credentialed
 * requests, which is the failure mode that looks like "CORS is broken" but is
 * actually "supports_credentials and allowed_origins disagree".
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const { baseUrl, authOrigin = baseUrl, onUnauthorized, onResponse: onResponseCallback } = options

  const client = createOpenApiClient<paths>({
    baseUrl,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  const auth: Middleware = {
    async onRequest({ request }) {
      for (const [name, value] of Object.entries(options.headers?.() ?? {})) {
        request.headers.set(name, value)
      }

      if (!needsCsrf(request.method)) {
        return request
      }

      if (!readCsrfToken()) {
        // The origin, not `baseUrl`: `/sanctum/csrf-cookie` is registered by
        // Sanctum on the `web` group at the API root, so it is NOT under the
        // `/api` prefix the typed paths carry.
        await ensureCsrfCookie(authOrigin)
      }

      const token = readCsrfToken()
      if (token) {
        request.headers.set(CSRF_HEADER, token)
      }

      return request
    },

    async onResponse({ response }) {
      onResponseCallback?.(response)
      lastRequestId = response.headers.get('X-Request-Id') ?? undefined

      if (response.ok) {
        return response
      }

      const error = await apiErrorFromResponse(response)

      if (error.kind === 'unauthorized') {
        onUnauthorized?.(error)
      }

      // openapi-fetch surfaces a thrown error to the caller's catch (and so to
      // TanStack Query's error state) rather than as a silent `{ error }`
      // branch every call site would have to remember to check.
      throw error
    },

    onError({ error }) {
      // Reaching here means fetch itself rejected: offline, DNS, TLS, aborted.
      if (error instanceof ApiError) {
        return error
      }

      return new ApiError({
        kind: 'network',
        status: 0,
        message: error instanceof Error ? error.message : 'Network request failed',
      })
    },
  }

  client.use(auth)

  return client
}
